import React from 'react';
import { Loader2, Mic, MicOff, Radio, Square, Volume2 } from 'lucide-react';

import { useEnglishLiveSession } from '../../hooks/useEnglishLiveSession';
import {
  EnglishLiveMode,
  EnglishLiveSessionCompletion,
  EnglishLiveSessionGrant,
  EnglishState,
} from '../../types/english';

interface EnglishLiveCoachCardProps {
  mode: EnglishLiveMode;
  englishState: EnglishState | null;
  grant: EnglishLiveSessionGrant | null;
  isIssuing: boolean;
  onReserve: (mode: EnglishLiveMode) => Promise<void> | void;
  onCompleted: (completion: EnglishLiveSessionCompletion) => Promise<void> | void;
}

const formatMinutes = (seconds: number): string => {
  const safeSeconds = Math.max(seconds || 0, 0);
  return `${Math.floor(safeSeconds / 60)}m ${safeSeconds % 60}s`;
};

export const EnglishLiveCoachCard: React.FC<EnglishLiveCoachCardProps> = ({
  mode,
  englishState,
  grant,
  isIssuing,
  onReserve,
  onCompleted,
}) => {
  const {
    status,
    error,
    isConnected,
    isConnecting,
    isMicActive,
    canConnect,
    elapsedSeconds,
    transcriptSummary,
    connect,
    startMicrophone,
    stopMicrophone,
    disconnect,
  } = useEnglishLiveSession({
    grant,
    onCompleted,
  });

  const enabled =
    mode === 'oral_reading'
      ? englishState?.english_oral_reading_enabled
      : englishState?.english_speaking_enabled;
  const title = mode === 'oral_reading' ? 'Oral Reading Coach' : 'Speaking Coach';
  const description =
    mode === 'oral_reading'
      ? 'Use Live practice for pronunciation, expression, decoding, and oral fluency outside the main reading path.'
      : 'Use Live practice for guided spoken English, confidence building, and short conversational drills.';
  const usedSeconds = mode === 'oral_reading' ? englishState?.live_usage?.oral_used_seconds || 0 : englishState?.live_usage?.speaking_used_seconds || 0;
  const capSeconds = mode === 'oral_reading' ? englishState?.live_usage?.oral_cap_seconds || 0 : englishState?.live_usage?.speaking_cap_seconds || 0;
  const sessionCapToday = mode === 'oral_reading' ? englishState?.live_usage?.oral_sessions_cap_today || 0 : englishState?.live_usage?.speaking_sessions_cap_today || 0;
  const sessionUsedToday = mode === 'oral_reading' ? englishState?.live_usage?.oral_sessions_used_today || 0 : englishState?.live_usage?.speaking_sessions_used_today || 0;
  const maxSessionSeconds = mode === 'oral_reading' ? englishState?.oral_session_max_seconds || 0 : englishState?.speaking_session_max_seconds || 0;

  return (
    <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">{title}</h3>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">{description}</p>
        </div>
        <button
          type="button"
          onClick={() => void onReserve(mode)}
          disabled={!enabled || isIssuing}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 disabled:opacity-60"
        >
          {isIssuing ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === 'oral_reading' ? <Mic className="h-4 w-4" /> : <Radio className="h-4 w-4" />}
          {grant ? 'Refresh Grant' : 'Reserve Session'}
        </button>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Minutes used</p>
          <p className="mt-2 text-sm text-[var(--text-primary)]">{formatMinutes(usedSeconds)} / {formatMinutes(capSeconds)}</p>
        </div>
        <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Sessions today</p>
          <p className="mt-2 text-sm text-[var(--text-primary)]">{sessionUsedToday} / {sessionCapToday}</p>
        </div>
        <div className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
          <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Max session</p>
          <p className="mt-2 text-sm text-[var(--text-primary)]">{formatMinutes(maxSessionSeconds)}</p>
        </div>
      </div>
      {grant ? (
        <div className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold bg-amber-500/10 border border-amber-500/20 text-amber-200">
              {grant.model}
            </span>
            <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-secondary)]">
              Expires {new Date(grant.expires_at).toLocaleTimeString()}
            </span>
            {isConnected ? (
              <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-emerald-500/10 border border-emerald-500/20 text-emerald-300">
                Connected · {formatMinutes(elapsedSeconds)}
              </span>
            ) : null}
          </div>

          {grant.reference_text ? (
            <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-4">
              <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">
                {mode === 'oral_reading' ? 'Read This Aloud' : 'Conversation Goal'}
              </p>
              <p className="mt-2 text-sm leading-6 text-[var(--text-primary)]">{grant.reference_text}</p>
            </div>
          ) : null}

          {grant.coach_goal ? (
            <p className="text-xs text-[var(--text-secondary)]">{grant.coach_goal}</p>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void connect()}
              disabled={!canConnect}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 disabled:opacity-60"
            >
              {isConnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />}
              {isConnected ? 'Connected' : 'Connect'}
            </button>
            <button
              type="button"
              onClick={() => void startMicrophone()}
              disabled={!isConnected || isMicActive}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 disabled:opacity-60"
            >
              <Mic className="h-4 w-4" />
              Start Mic
            </button>
            <button
              type="button"
              onClick={stopMicrophone}
              disabled={!isMicActive}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-700 text-white text-sm font-medium hover:bg-slate-600 disabled:opacity-60"
            >
              <MicOff className="h-4 w-4" />
              Stop Mic
            </button>
            <button
              type="button"
              onClick={() => void disconnect()}
              disabled={!grant}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-500 text-white text-sm font-medium hover:bg-rose-600 disabled:opacity-60"
            >
              <Square className="h-4 w-4" />
              End Session
            </button>
          </div>

          {error ? (
            <p className="text-sm text-rose-300">{error}</p>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-4">
              <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Learner transcript</p>
              <p className="mt-2 text-sm text-[var(--text-primary)] whitespace-pre-wrap">
                {transcriptSummary.input || 'Your spoken words will appear here once the Live session hears you.'}
              </p>
            </div>
            <div className="rounded-xl border border-[var(--border-color)] bg-[var(--surface-elevated)] p-4">
              <p className="text-xs uppercase tracking-wide text-[var(--text-tertiary)]">Coach transcript</p>
              <p className="mt-2 text-sm text-[var(--text-primary)] whitespace-pre-wrap">
                {transcriptSummary.output || 'The coach’s spoken feedback will appear here.'}
              </p>
            </div>
          </div>

          <p className="text-xs text-[var(--text-secondary)]">
            Status: <span className="capitalize text-[var(--text-primary)]">{status.replace('_', ' ')}</span>
          </p>
        </div>
      ) : null}
    </div>
  );
};
